import { createModel } from '@rematch/core';
import { Dispatch } from '../../store';
import { Allergy } from '../../models/allergy';
import { PatientHistory } from '../../models/patient-history';
import {
    PatientPortalData,
    PatientPortalDataType
} from '../../models/patient-portal-data';
import { PatientRx } from '../../models/patient-rx';
import { JsonParse } from '../../utils/converter';
import { getPatientPortalDatas } from '../../apis/patient-portal-data';

export interface State {
    byCode: Dictionary<PatientPortalData>;  // code: type + related id
    loading: Dictionary<boolean>;
}

export const patientPortalDatas = createModel<State>({
    state: {
        byCode: {},
        loading: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<PatientPortalData>>,
            loadingKey?: string
        ): State => ({
            ...state,
            byCode: {
                ...state.byCode,
                ...processData(data)
            },
            ...loadingKey && {
                loading: {
                    ...state.loading,
                    [loadingKey]: false
                }
            }
        }),
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        })
    },
    effects: dispatch => ({
        async getPatientPortalDatasAsync(type?: PatientPortalDataType) {
            const {
                compositeAllergies,
                compositeDrugs,
                icd10s,
                snomeds
                // @ts-ignore
            } = dispatch as Dispatch;
            this.loading('getPatientPortalDatasAsync', false);
            try {
                const data = await getPatientPortalDatas(type);
                this.update(data, 'getPatientPortalDatasAsync');

                if (data) {
                    const patientHistories = filterPatientHistoriesData(data);
                    snomeds.getSnomedsAsync({
                        conceptIds: patientHistories
                            .filter(x => x.type === PatientPortalDataType.SocialHistory)
                            .map(x => JsonParse<PatientHistory>(x.metadata).code),
                        active: true
                    });
                    snomeds.getSnomedsAsync({
                        conceptIds: patientHistories.filter(x =>
                            x.type !== PatientPortalDataType.FamilyHistory &&
                            x.type !== PatientPortalDataType.PastMedicalHistory &&
                            x.type !== PatientPortalDataType.DrinkingHistory &&
                            x.type !== PatientPortalDataType.SmokingHistory
                        ).map(x => JsonParse<PatientHistory>(x.metadata).code),
                        active: false
                    });
                    icd10s.getICD10sAsync(patientHistories.filter(x =>
                        x.type === PatientPortalDataType.FamilyHistory ||
                        x.type === PatientPortalDataType.PastMedicalHistory
                    ).map(x => JsonParse<PatientHistory>(x.metadata).icd10Code));

                    const allergies = data.filter(x => x.type === PatientPortalDataType.Allergy);
                    compositeAllergies.getCompositeAllergiesAsync(allergies.map(x => JsonParse<Allergy>(x.metadata).allergyId));

                    const patientRxes = data.filter(x => x.type === PatientPortalDataType.Medication);
                    compositeDrugs.getCompositeDrugsAsync(patientRxes.map(x => JsonParse<PatientRx>(x.metadata).medId));
                }
            } catch (e) {
                window.console.log(e);
                this.loading('getPatientPortalDatasAsync', false);
            }
        }
    })
});

const processData = (data: Undefinable<MixType<PatientPortalData>>): Dictionary<PatientPortalData> => {
    if (data) {
        if (Array.isArray(data)) {
            data.sort((a, b) => a.relatedCreatedDate > b.relatedCreatedDate ? 1 : -1);
            return data.reduce(
                (prev, x) => {
                    prev[`${x.type || ''}---${x.relatedId || ''}`] = {
                        ...x,
                        dataModel: JsonParse(x.metadata)
                    };
                    return prev;
                },
                {} as Dictionary<PatientPortalData>
            );
        } else {
            return {
                [`${data.type || ''}---${data.relatedId || ''}`]: {
                    ...data,
                    dataModel: JsonParse(data.metadata)
                }
            } as Dictionary<PatientPortalData>;
        }
    } else {
        return {} as Dictionary<PatientPortalData>;
    }
};

const filterPatientHistoriesData = (data: PatientPortalData[]): PatientPortalData[] => {
    return data.filter(x =>
        x.type === PatientPortalDataType.PastMedicalHistory ||
        x.type === PatientPortalDataType.FamilyHistory ||
        x.type === PatientPortalDataType.SocialHistory ||
        x.type === PatientPortalDataType.SmokingHistory ||
        x.type === PatientPortalDataType.DrinkingHistory ||
        x.type === PatientPortalDataType.DrugAbuseHistory ||
        x.type === PatientPortalDataType.FallHistory
    );
};
